import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FaBuilding,
  FaCalendarAlt,
  FaEnvelope,
  FaGlobe,
  FaIndustry,
  FaKey,
  FaLock,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUserPlus,
  FaUserTie,
} from 'react-icons/fa';
import workspaceAPI from '../../services/workspaceAPI';
import { registerPushNotifications } from '../../services/pushNotificationService';
import { syncWorkspaceAccessFromResponse } from '../../utils/workspaceSession';
import './WorkspaceJoinPage.css';

const normalizeToken = (raw) => String(raw || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase();

const formatInviteDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="ws-invite-detail-row">
      <span className="ws-invite-detail-icon" aria-hidden="true">
        <Icon />
      </span>
      <div>
        <span className="ws-invite-detail-label">{label}</span>
        <span className="ws-invite-detail-value">{value}</span>
      </div>
    </div>
  );
};

const WorkspaceInvitePage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const inviteToken = useMemo(() => {
    const fromQuery = searchParams.get('token');
    const fromSplat = params['*'];
    const fromPath =
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/^\/workspace\/invite\/?/, '')
        : '';
    return normalizeToken(fromQuery || fromSplat || fromPath);
  }, [params, searchParams]);

  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    if (!inviteToken || inviteToken.length < 32) {
      setError('Invalid invitation link. Ask your company admin to send a new invite.');
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setError('');
    workspaceAPI
      .getStaffInvitationPreview(inviteToken)
      .then((res) => setPreview(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Invalid or expired invitation'))
      .finally(() => setPreviewLoading(false));
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await workspaceAPI.activateStaffInvitation(inviteToken, {
        password,
        name: name.trim(),
      });
      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        syncWorkspaceAccessFromResponse(res.data);
        window.dispatchEvent(new CustomEvent('codex-authenticated'));
        await registerPushNotifications({ requestPermission: true }).catch(() => {});
        navigate('/workspace', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to activate account');
    } finally {
      setLoading(false);
    }
  };

  if (previewLoading) {
    return (
      <div className="ws-join-page">
        <p style={{ position: 'relative', zIndex: 1, color: '#334155' }}>Loading your invitation…</p>
      </div>
    );
  }

  if (!preview && error) {
    return (
      <div className="ws-join-page">
        <div className="ws-join-shell" style={{ maxWidth: 520, gridTemplateColumns: '1fr' }}>
          <main className="ws-join-card">
            <header className="ws-join-card-header">
              <h1>Invitation unavailable</h1>
            </header>
            <div className="ws-join-alert" role="alert">
              {error}
            </div>
            <p style={{ marginTop: 16, color: '#64748b' }}>
              You can also join using the workspace code at{' '}
              <Link to="/join-workspace">Join Workspace</Link>.
            </p>
            <Link to="/" style={{ marginTop: 12, display: 'inline-block' }}>
              ← Back to home
            </Link>
          </main>
        </div>
      </div>
    );
  }

  const invitedByLabel =
    preview?.invitedByName && preview?.invitedByEmail
      ? `${preview.invitedByName} (${preview.invitedByEmail})`
      : preview?.invitedByName || preview?.invitedByEmail || 'Company admin';

  const fullAddress = [
    preview?.addressLine1,
    preview?.addressLine2,
    preview?.location,
    preview?.postalCode,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="ws-join-page">
      <div className="ws-join-bg" aria-hidden="true">
        <span className="ws-join-orb ws-join-orb-1" />
        <span className="ws-join-orb ws-join-orb-2" />
        <span className="ws-join-orb ws-join-orb-3" />
      </div>

      <div className="ws-join-shell ws-invite-shell">
        <aside className="ws-join-aside">
          <div className="ws-join-brand">
            {preview?.logoUrl ? (
              <img src={preview.logoUrl} alt="" className="ws-invite-company-logo" />
            ) : (
              <span className="ws-join-brand-icon">
                <FaBuilding />
              </span>
            )}
            <div>
              <p className="ws-join-brand-label">UBSAAPP Workspace</p>
              <h2 className="ws-join-brand-title">{preview?.companyName || 'Your team'}</h2>
              {preview?.companyLegalName ? (
                <p className="ws-invite-legal-name">{preview.companyLegalName}</p>
              ) : null}
            </div>
          </div>

          <div className="ws-join-company-pill">
            <span>
              Role: <strong>{preview?.roleLabel}</strong>
            </span>
          </div>

          {preview?.companyDescription ? (
            <p className="ws-join-aside-lead">{preview.companyDescription}</p>
          ) : (
            <p className="ws-join-aside-lead">
              You have been invited to join this company workspace on UBSAAPP.
            </p>
          )}

          <div className="ws-invite-details ws-invite-details--aside">
            <DetailRow icon={FaIndustry} label="Industry" value={preview?.industry} />
            <DetailRow icon={FaMapMarkerAlt} label="Location" value={fullAddress || preview?.location} />
            <DetailRow icon={FaGlobe} label="Website" value={preview?.website} />
            <DetailRow icon={FaEnvelope} label="Company email" value={preview?.contactEmail} />
            <DetailRow icon={FaPhone} label="Company phone" value={preview?.contactPhone} />
            <DetailRow icon={FaKey} label="Workspace code" value={preview?.workspaceCode} />
            <DetailRow icon={FaUserTie} label="Invited by" value={invitedByLabel} />
            <DetailRow icon={FaCalendarAlt} label="Invite sent" value={formatInviteDate(preview?.invitedAt)} />
            <DetailRow icon={FaCalendarAlt} label="Valid until" value={formatInviteDate(preview?.expiresAt)} />
          </div>
        </aside>

        <main className="ws-join-card">
          <header className="ws-join-card-header">
            <h1>
              <FaUserPlus style={{ marginRight: 8 }} />
              Accept invitation
            </h1>
            <p>Create your password to activate your staff account.</p>
          </header>

          <section className="ws-invite-summary" aria-label="Invitation details">
            <h2 className="ws-invite-summary-title">Invitation details</h2>
            <div className="ws-invite-details ws-invite-details--card">
              <DetailRow icon={FaEnvelope} label="Your account email" value={preview?.email} />
              <DetailRow icon={FaUserTie} label="Your role" value={preview?.roleLabel} />
              <DetailRow icon={FaBuilding} label="Company" value={preview?.companyName} />
              {preview?.companyLegalName ? (
                <DetailRow icon={FaBuilding} label="Registered name" value={preview.companyLegalName} />
              ) : null}
              <DetailRow icon={FaUserTie} label="Invited by" value={invitedByLabel} />
              <DetailRow icon={FaKey} label="Workspace code" value={preview?.workspaceCode} />
              <DetailRow icon={FaIndustry} label="Industry" value={preview?.industry} />
              <DetailRow icon={FaMapMarkerAlt} label="Location" value={fullAddress || preview?.location} />
              <DetailRow icon={FaGlobe} label="Website" value={preview?.website} />
              <DetailRow icon={FaEnvelope} label="Company contact" value={preview?.contactEmail} />
              <DetailRow icon={FaPhone} label="Company phone" value={preview?.contactPhone} />
              <DetailRow icon={FaCalendarAlt} label="Invite expires" value={formatInviteDate(preview?.expiresAt)} />
            </div>
          </section>

          {error && (
            <div className="ws-join-alert" role="alert">
              {error}
            </div>
          )}

          <form className="ws-join-form" onSubmit={handleSubmit}>
            <div className="ws-join-field">
              <label htmlFor="invite-name">
                <FaUser style={{ marginRight: 6 }} />
                Your name
              </label>
              <input
                id="invite-name"
                className="ws-join-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="ws-join-field">
              <label htmlFor="invite-password">
                <FaLock style={{ marginRight: 6 }} />
                Create password
              </label>
              <input
                id="invite-password"
                className="ws-join-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="ws-join-field">
              <label htmlFor="invite-confirm">Confirm password</label>
              <input
                id="invite-confirm"
                className="ws-join-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="ws-join-submit" disabled={loading}>
              {loading ? 'Activating…' : 'Activate & go to dashboard'}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
};

export default WorkspaceInvitePage;
